import axios from "axios";
import { getToken } from "firebase/app-check";
import { auth, appCheck } from "./firebase";

// #region Constants — API base URL from env, localStorage keys for token persistence, 5-min refresh buffer (must match AuthContext values)
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.matchmixer.co.uk";

const TOKEN_KEY = "firebaseIdToken";
const TOKEN_EXPIRY_KEY = "firebaseTokenExpiry";
const TOKEN_REFRESH_BUFFER = 5 * 60 * 1000; // Refresh 5 minutes before expiry
// #endregion

// #region Axios instance — single shared instance with baseURL; all API calls in the app import this rather than raw axios
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});
// #endregion

// #region Request interceptor — injects Authorization: Bearer token on every outgoing request; proactively force-refreshes the token when within TOKEN_REFRESH_BUFFER of expiry
axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      const user = auth.currentUser;
      if (user) {
        // Check if token needs refresh
        const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
        if (tokenExpiry) {
          const expiryTime = parseInt(tokenExpiry, 10);
          const currentTime = Date.now();
          const timeUntilExpiry = expiryTime - currentTime;

          // If token expires within buffer time, refresh it
          if (timeUntilExpiry < TOKEN_REFRESH_BUFFER) {
            const newToken = await user.getIdToken(true); // Force refresh
            const newExpiry = Date.now() + 60 * 60 * 1000; // 1 hour from now
            localStorage.setItem(TOKEN_KEY, newToken);
            localStorage.setItem(TOKEN_EXPIRY_KEY, newExpiry.toString());
          }
        }

        // Get current token from localStorage
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      // Attach App Check token if initialised
      if (appCheck) {
        try {
          const appCheckResult = await getToken(appCheck, false);
          if (appCheckResult?.token) {
            config.headers["X-Firebase-AppCheck"] = appCheckResult.token;
          }
        } catch (err) {
          // Non-fatal — backend runs in soft mode until APP_CHECK_ENFORCEMENT=true
          console.warn("App Check token unavailable:", err.message);
        }
      }
    } catch (error) {
      console.error("Error in request interceptor:", error);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);
// #endregion

// #region Response interceptor — on 401, force-refreshes the Firebase token once and retries the original request; clears localStorage and rejects if the refresh itself fails
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retried, try to refresh token and retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const user = auth.currentUser;
        if (user) {
          const newToken = await user.getIdToken(true); // Force refresh
          const newExpiry = Date.now() + 60 * 60 * 1000; // 1 hour from now
          localStorage.setItem(TOKEN_KEY, newToken);
          localStorage.setItem(TOKEN_EXPIRY_KEY, newExpiry.toString());

          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        console.error("Error refreshing token:", refreshError);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
        // Redirect to login or dispatch auth error
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);
// #endregion

export default axiosInstance;
